import React, { useContext } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  createTheme,
  useMediaQuery,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { useChangePassword } from "../../hooks/query/useUser";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/authContext";

interface PasswordFormInputs {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const ChangePasswordForm: React.FC = () => {
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = useForm<PasswordFormInputs>();
  const { currentUser } = useContext(AuthContext);

  const { mutate: changePassword } = useChangePassword();

  const onSubmit = (data: PasswordFormInputs) => {
    console.log("Form Data:", data);
    changePassword(
      {
        email: currentUser.email,
        oldPassword: data.currentPassword,
        newPassword: data.newPassword,
      },
      {
        onSuccess: () => {
          toast.success("Password changed successfully");
          reset();
        },
        onError: (error: any) => {
          console.error("Error creating property:", error);
          toast.error(error?.body?.error?.message || "An error occurred");
        },
      }
    );
  };

  const newPasswordValue = watch("newPassword", "");

  const theme = createTheme({
    palette: {
      primary: {
        main: "#bb0000",
        light: "#e63434",
        dark: "#880000",
      },
      background: {
        default: "#f4f4f4",
      },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      h5: {
        fontSize: "1.5rem",
        "@media (max-width:600px)": {
          fontSize: "1.2rem",
        },
      },
    },
    components: {
      MuiTextField: {
        styleOverrides: {
          root: {
            "@media (max-width:600px)": {
              marginBottom: "16px",
            },
          },
        },
      },
    },
  });

  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box>
      <Typography variant="h5" color="primary" gutterBottom>
        Change Password
      </Typography>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={isSmallScreen ? 2 : 3}>
          <Grid item xs={12}>
            <Controller
              name="currentPassword"
              control={control}
              defaultValue=""
              rules={{ required: "Current Password is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Current Password"
                  type="password"
                  variant="outlined"
                  error={!!errors.currentPassword}
                  helperText={errors.currentPassword?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="newPassword"
              control={control}
              defaultValue=""
              rules={{
                required: "New Password is required",
                minLength: {
                  value: 6,
                  message: "Password must be at least 6 characters long",
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="New Password"
                  type="password"
                  variant="outlined"
                  error={!!errors.newPassword}
                  helperText={errors.newPassword?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="confirmPassword"
              control={control}
              defaultValue=""
              rules={{
                required: "Please confirm your password",
                validate: (value) =>
                  value === newPasswordValue || "Passwords do not match",
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Confirm New Password"
                  type="password"
                  variant="outlined"
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              sx={{ mt: 2 }}
            >
              Update Password
            </Button>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default ChangePasswordForm;
